import type {
  IAreaWithSelectedPoints,
  IBaseFloor,
  IIssueLocation,
  ISelectedPoint,
} from '@d-orca/shared/trusstor';

export interface IAreaView extends Pick<IAreaWithSelectedPoints, 'areaId' | 'areaNick'> {
  pinCount: number;
}

export interface IFloorLocationView extends IBaseFloor {
  /** Areas the issue touches on this floor, first-seen order, deduped by id. */
  areas: IAreaView[];
  /** Every area the issue touches on this floor — painted as the tint. */
  selectedAreaIds: Set<string>;
  /** Pins per areaId, only for areas that carry explicit points. */
  pinsByArea: Record<string, ISelectedPoint[]>;
}

/**
 * Collapses an issue's flat `locations` array into one entry per floor, in
 * first-seen order. A location can be a whole area (tint only) or a pinned
 * point — so every area feeds `selectedAreaIds` (the wash) and any
 * `selectedPoints` also become pins. `areas` keeps the human-readable nicks so
 * the viewer can name what's highlighted. This matches the shape FloorPlanCanvas
 * already consumes in the create/edit dialog.
 *
 * `IIssueLocation[]` carries no schema-level uniqueness guarantee, so an area
 * may legitimately appear more than once for the same floor — pins are
 * accumulated across every occurrence and `pinCount` is derived from the merged
 * total, rather than trusting the first entry.
 */
export function groupLocationsByFloor(locations: IIssueLocation[]): IFloorLocationView[] {
  const byFloor = new Map<string, IFloorLocationView>();
  for (const location of locations) {
    const floorId = location.floor?.floorId;
    const areaId = location.area?.areaId;
    // Skip malformed entries before creating a bucket, so an area-less location
    // can't leave an empty "ghost" floor that renders as a selectable tab.
    if (!floorId || !areaId) continue;

    let view = byFloor.get(floorId);
    if (!view) {
      view = {
        floorId,
        floorNick: location.floor.floorNick ?? floorId,
        areas: [],
        selectedAreaIds: new Set(),
        pinsByArea: {},
      };
      byFloor.set(floorId, view);
    }

    // Runtime-defensive: the frontend consumes issues via a bare cast, not a
    // zod parse, so `selectedPoints` could be a non-array on a malformed payload.
    const points = Array.isArray(location.area.selectedPoints) ? location.area.selectedPoints : [];
    if (points.length > 0) {
      view.pinsByArea[areaId] = [...(view.pinsByArea[areaId] ?? []), ...points];
    }
    if (!view.selectedAreaIds.has(areaId)) {
      view.selectedAreaIds.add(areaId);
      view.areas.push({ areaId, areaNick: location.area.areaNick ?? areaId, pinCount: 0 });
    }
  }

  // Derive pinCount from the merged pins, after all occurrences are folded in.
  for (const view of byFloor.values()) {
    for (const area of view.areas) {
      area.pinCount = view.pinsByArea[area.areaId]?.length ?? 0;
    }
  }
  return Array.from(byFloor.values());
}
